import { motion, useReducedMotion } from 'framer-motion';
import { DISTANCE, STAGGER, VIEWPORT, groupVariants, revealVariants } from '../../lib/motion';

/**
 * Standard entrance: fade up as it enters the viewport, once.
 * Reads all timings from lib/motion so every section moves identically.
 *
 * `as` preserves the semantic tag — a wrapper that must stay a <dl>, <ul> or <section>
 * cannot silently become a <div> without orphaning its children.
 */
export function Reveal({
  as = 'div',
  distance = DISTANCE,
  delay = 0,
  className,
  children,
  ...rest
}) {
  const reduceMotion = useReducedMotion();

  if (reduceMotion) {
    const Tag = as;
    return (
      <Tag className={className} {...rest}>
        {children}
      </Tag>
    );
  }

  const MotionTag = motion[as] ?? motion.div;

  return (
    <MotionTag
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={VIEWPORT}
      variants={revealVariants(distance, delay)}
      {...rest}
    >
      {children}
    </MotionTag>
  );
}

/**
 * Staggers RevealItem children. Use for lists — chips, card grids, timeline rows.
 * Children must be RevealItem; they inherit the visible state from this parent.
 */
export function RevealGroup({ as = 'div', stagger = STAGGER, className, children, ...rest }) {
  const reduceMotion = useReducedMotion();

  if (reduceMotion) {
    const Tag = as;
    return (
      <Tag className={className} {...rest}>
        {children}
      </Tag>
    );
  }

  const MotionTag = motion[as] ?? motion.div;

  return (
    <MotionTag
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={VIEWPORT}
      variants={groupVariants(stagger)}
      {...rest}
    >
      {children}
    </MotionTag>
  );
}

export function RevealItem({ as = 'div', distance = DISTANCE, className, children, ...rest }) {
  const reduceMotion = useReducedMotion();

  if (reduceMotion) {
    const Tag = as;
    return (
      <Tag className={className} {...rest}>
        {children}
      </Tag>
    );
  }

  const MotionTag = motion[as] ?? motion.div;

  return (
    <MotionTag className={className} variants={revealVariants(distance)} {...rest}>
      {children}
    </MotionTag>
  );
}
